// Module 3.2b — Drill String Hub.
//
// Lists every Drill String owned by a single well. Reached from
// `/wells/:wellId/drill-strings`. Mirrors the BHA Hub layout so the user
// only has to learn the pattern once.

import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Plus, ArrowLeft, Trash2, Layers, Lock,
} from "lucide-react";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import { listDrillStringsForWell, deleteDrillString } from "@/lib/drillstring/storage";
import { computeDrillStringGeometry } from "@/lib/drillstring/schema";
import { useStackMode } from "@/lib/stackModeContext";
import { stackAccentClasses, stackAccentColor } from "@/lib/stackAccent";

const ASSEMBLY_COLOR = {
  draft:    "border-zinc-500/40 text-zinc-300",
  approved: "border-emerald-500/40 text-emerald-300",
  locked:   "border-amber-500/40 text-amber-400",
};
const OPERATIONAL_COLOR = {
  planned:   "border-sky-500/40 text-sky-300",
  "in-hole": "border-amber-500/50 text-amber-400",
  racked:    "border-zinc-500/40 text-zinc-300",
  retired:   "border-zinc-700/40 text-zinc-500",
};

function Pill({ value, palette, testId, locked }) {
  const cls = palette[value] || "border-zinc-500/40 text-zinc-400";
  return (
    <span data-testid={testId}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-[0.16em] ${cls}`}>
      {locked && <Lock className="w-2.5 h-2.5" aria-hidden="true" />}
      {value}
    </span>
  );
}

const _fmt = (v, digits = 0) => {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

export default function DrillStringHub() {
  const { stackMode } = useStackMode();
  const accent = stackAccentClasses(stackMode);
  const navigate = useNavigate();
  const { wellId } = useParams();
  const [well, setWell] = useState(() => getWellProfile(wellId));
  const [rows, setRows] = useState(() => listDrillStringsForWell(wellId));

  useEffect(() => {
    const onFocus = () => {
      setWell(getWellProfile(wellId));
      setRows(listDrillStringsForWell(wellId));
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [wellId]);

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — see KillSheetOpHub for rationale.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to view its drill strings, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }

  const handleDelete = (id, name) => {
    if (!window.confirm(`Delete drill string "${name || "(unnamed)"}"?`)) return;
    deleteDrillString(id);
    setRows(listDrillStringsForWell(wellId));
  };

  return (
    <div data-testid="ds-hub" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <Link to={`/wells/${wellId}`}
              data-testid="ds-hub-back"
              className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> {well.wellName || "Well"}
        </Link>
        <Link to={`/wells/${wellId}/drill-strings/new`}
              data-testid="ds-hub-new"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
              style={{ background: stackAccentColor(stackMode), color: "#0A0B0E" }}>
          <Plus className="w-3.5 h-3.5" /> New Drill String
        </Link>
      </header>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
          Module 3.2b · {well.wellName || "Well"}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-0.5 text-[var(--rs-text,#e8edf2)]">
          Drill Strings
        </h1>
        <p className="text-xs text-[var(--rs-text-dim,#9aa6b2)] mt-1 leading-relaxed">
          Tubulars above the BHA — drill pipe, HWDP, in-string crossovers.
          Internal volume feeds the Kill Sheet&apos;s Surface→bit input on re-pull.
        </p>
      </div>

      {rows.length === 0 ? (
        <div data-testid="ds-hub-empty"
             className="card-rs p-6 text-center text-sm text-[var(--rs-text-dim,#9aa6b2)]">
          No drill strings yet for this well. Tap{" "}
          <span className={`font-medium ${accent.text}`}>+ New Drill String</span> to define your tally.
        </div>
      ) : (
        <div data-testid="ds-hub-list" className="flex flex-col gap-2">
          {rows.map((s) => {
            // listDrillStringsForWell returns full records; bake geometry once
            // for the live summary chip on each row.
            const baked = computeDrillStringGeometry(s);
            return (
              <div key={s.id}
                   data-testid={`ds-row-${s.id}`}
                   className="card-rs p-3 flex items-start gap-3">
                <Link to={`/wells/${wellId}/drill-strings/${s.id}`}
                      data-testid={`ds-open-${s.id}`}
                      className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Layers className={`w-3.5 h-3.5 shrink-0 ${accent.text}`} aria-hidden="true" />
                    <span className="font-semibold text-sm text-[var(--rs-text,#e8edf2)] truncate">
                      {s.name || "(unnamed)"}
                    </span>
                    {s.revision && (
                      <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-zinc-500/40 text-zinc-300">
                        Rev {s.revision}
                      </span>
                    )}
                    <Pill value={s.assemblyStatus} palette={ASSEMBLY_COLOR}
                          locked={s.assemblyStatus === "locked"}
                          testId={`ds-row-${s.id}-asm`} />
                    <Pill value={s.operationalStatus} palette={OPERATIONAL_COLOR}
                          testId={`ds-row-${s.id}-op`} />
                  </div>
                  <div className="mt-1 text-[11px] text-[var(--rs-text-dim,#9aa6b2)] flex items-center gap-2 flex-wrap">
                    {s.stringNumber && <span>DS #{s.stringNumber}</span>}
                    {s.runNumber    && <span>· Run {s.runNumber}</span>}
                    <span>· {baked.summary.sectionCount} section{baked.summary.sectionCount === 1 ? "" : "s"}</span>
                    <span>· {_fmt(baked.summary.totalLengthFt, 0)} ft</span>
                    <span>· {_fmt(baked.summary.totalInternalVolumeBbl, 2)} bbl</span>
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[var(--rs-text-3,#6b7785)]">
                    Updated {s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : "—"}
                  </div>
                </Link>
                <button type="button"
                        onClick={() => handleDelete(s.id, s.name)}
                        data-testid={`ds-delete-${s.id}`}
                        aria-label={`Delete ${s.name}`}
                        className="shrink-0 p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
